/*!

=========================================================
* Argon Dashboard PRO React - v1.2.5
=========================================================

* Product Page: https://www.easyhub.ai/product/argon-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.easyhub.ai)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
import { useLocation, Route, Routes, useNavigate } from "react-router-dom";
import AdminNavbar from "../components/Navbars/AdminNavbar";
import BottomNav from "../components/BottomNav/BottomNav";
import routes from "../routes";
import { useAuth } from "../hooks/useAuth";
import { isNotNullOrEmpty } from "../util/null-helper";
import { AuthContext, AuthContextType } from "../contexts/auth-context";

function Admin() {
  const [sidenavOpen, setSidenavOpen] = React.useState(true);
  const location = useLocation();
  const mainContentRef = React.useRef(null);
  const { user, cleanUp } = React.useContext<AuthContextType>(AuthContext);
  const { token, loaded, logout, role } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [location]);

  React.useEffect(() => {
    if (loaded === true) {
      if (!isNotNullOrEmpty(token)) {
        navigate('/welcome');
      }
    }
  }, [loaded, navigate, token]);

  const getRoutes = (routes, role) => {
    return routes.map((prop, key) => {
      if (prop.collapse) {
        return getRoutes(prop.views, role);
      }
      if (prop.layout === "/admin" && (prop.roles === undefined || (prop.roles && prop.roles.indexOf(role) >= 0))) {
        return (
          <Route path={prop.path} element={prop.component} key={key} />
        );
      } else {
        return null;
      }
    });
  };

  const toggleSidenav = () => {
    if (document.body.classList.contains("g-sidenav-pinned")) {
      document.body.classList.remove("g-sidenav-pinned");
      document.body.classList.add("g-sidenav-hidden");
    } else {
      document.body.classList.add("g-sidenav-pinned");
      document.body.classList.remove("g-sidenav-hidden");
    }
    setSidenavOpen(!sidenavOpen);
  };

  const getNavbarTheme = () => {
    return location.pathname.indexOf("admin/alternative-dashboard") === -1
      ? "dark"
      : "light";
  };

  const handleLogout = () => {
    cleanUp();
    logout();
    navigate('/welcome');
  };

  // Full-screen pages that hide navbar and bottom nav
  const fullScreenPages = ['/admin/stay', '/admin/deal', '/admin/service'];
  const isFullScreenPage = fullScreenPages.some(page => location.pathname.startsWith(page));

  const displayName = user?.name || "Guest";

  return (
    <>
      <div className="main-content" ref={mainContentRef} style={{ minHeight: '100vh' }}>
        {/* Hide navbar on full-screen pages */}
        {!isFullScreenPage && (
          <AdminNavbar
            theme={getNavbarTheme()}
            toggleSidenav={toggleSidenav}
            sidenavOpen={sidenavOpen}
            fullName={displayName}
            logout={handleLogout}
            role={role}
            cleanup={cleanUp}
            partnerLogo={user?.partnerLogo}
            partnerName={user?.partnerName}
          />
        )}
        <Routes>
          {getRoutes(routes, role)}
        </Routes>
      </div>

      {/* Bottom Navigation - Modern iOS Floating Pill Style */}
      {!isFullScreenPage && <BottomNav />}

      {sidenavOpen ? (
        <div className="backdrop d-xl-none" onClick={toggleSidenav} />
      ) : null}
    </>
  );
}

export default Admin;
